'use client';

import type React from 'react';

import { useState } from 'react';
import { Button } from '@heroui/button';
import { Input, Textarea } from '@heroui/input';
import CountrySelector from '@/components/countrySelect';
import { Select, SelectItem } from '@heroui/react';

export default function ContactUsPage() {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    country: '',
    telephone: '',
    email: '',
    interest: '',
    message: '',
  });

  const [wordCount, setWordCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const maxWords = 500;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'message') {
      const wordArray = value.split(/\s+/).filter(Boolean);
      if (wordArray.length <= maxWords) {
        setWordCount(wordArray.length); // Update word count
        setFormData((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      }
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleCountryChange = (country: string) => {
    setFormData((prevState) => ({
      ...prevState,
      country,
    }));
  };

  const handleInterestChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      interest: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await fetch('/api/become-agent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Your application has been submitted successfully!',
        });
        setFormData({
          name: '',
          address: '',
          country: '',
          telephone: '',
          email: '',
          interest: '',
          message: '',
        });
        setWordCount(0);
      } else {
        const errorData = await response.json();
        setSubmitStatus({
          type: 'error',
          message: errorData.error || 'Failed to send the message. Please try again later.',
        });
      }
    } catch (error) {
      console.error(error);
      setSubmitStatus({
        type: 'error',
        message: 'An error occurred. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      address: '',
      country: '',
      telephone: '',
      email: '',
      interest: '',
      message: '',
    });
    setWordCount(0);
    setSubmitStatus({ type: null, message: '' });
  };

  return (
    <section className="section-md">
      <div className="container container-md">
        <div className="space-y-8">
          <h2 className="text-3xl font-semibold text-center">Become an Agent</h2>

          {submitStatus.type && (
            <div
              className={`p-4 rounded-md ${submitStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
            >
              {submitStatus.message}
            </div>
          )}

          <form className="max-w-2xl mx-auto space-y-6" onSubmit={handleSubmit}>
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <Input
                  isRequired
                  id="name"
                  label="Name"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Input
                  isRequired
                  id="address"
                  label="Address"
                  name="address"
                  placeholder="Full Address"
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <CountrySelector value={formData.country} onChange={handleCountryChange} />
              </div>
              <div>
                <Input
                  id="telephone"
                  label="Telephone"
                  name="telephone"
                  placeholder="Your phone number"
                  type="tel"
                  value={formData.telephone}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Input
                  isRequired
                  id="email"
                  label="Email address"
                  name="email"
                  placeholder="your@email.com"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Select
                  isRequired
                  id="interest"
                  label="Interest"
                  name="interest"
                  placeholder="Select"
                  value={formData.interest}
                  onChange={handleInterestChange}
                >
                  <SelectItem key="Super Agent">Super Agent</SelectItem>
                  <SelectItem key="Sub Agent">Sub Agent</SelectItem>
                </Select>
              </div>
            </div>
            <div>
              <Textarea
                id="message"
                label="Add Note (Optional, max 500 words)"
                name="message"
                placeholder="Your message here..."
                rows={6}
                value={formData.message}
                onChange={handleInputChange}
              />
              <div className="mt-2 text-sm text-gray-500">
                {wordCount}/{maxWords} words
              </div>
            </div>
            <div className="flex-row justify-between gap-6 md:flex">
              <div>
                <Button color="primary" type="submit" disabled={isSubmitting} onPress={() => {}}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
